/**
 * Catalogue views and shared form utilities for Cibles.
 *
 * Contains:
 *   - ChipListEditor         — shared chip input used by all add/edit dialogs
 *   - TagChips               — read-only tag chip list (used by TargetTableRow and CollectionListView)
 *   - GenericTargetDialog    — add/edit dialog for non-custom categories
 *   - productDomains         — extract unique domains from a product row
 *   - extractDomain / getUrlPath — URL helpers for contenu domain grouping
 *   - ProductListView        — product catalogue list
 *   - ProductAddForm         — add/edit dialog for a single product
 *   - CollectionListView     — compact catalogue collection list
 *   - ContenuDomainView      — domain-grouped display for the contenu tab
 *
 * This file sits at the bottom of the import graph: EstablishmentForm, PersonneForm,
 * and TargetTableRow all import from here but CatalogueViews imports from none of them.
 */
import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { useTranslation } from 'react-i18next';
import DomainFavicon from '../../components/competitors/DomainFavicon';
import { FIELD_REGISTRY, TARGET_LIMITS, registrableLabel } from '@traaker/shared';
import { api } from '../../services/api';
import { targetsService } from '../../services/targets';
import {
  type TargetRow, type TagRegistry, type CatConfig, type ColDef,
  tagNamesOf, colorHex, CAT_BY_ID,
} from './CATEGORIES';
import { normalizeProductUrl, normalizeDomainInput } from './csv';
import {
  K, microLabel, Pill, WarnPill, rowActionSx,
  DialogHead, DialogFoot, FieldLabel, dialogPaperProps, dialogContentSx,
  inputSx, selectSx, dropdownSx, primaryBtnSx, secondaryBtnSx,
} from './ui';

const MONO = 'ui-monospace, "SF Mono", Menlo, monospace';

// ---------------------------------------------------------------------------
// ChipListEditor — alias and tag chip input used across all form dialogs
// ---------------------------------------------------------------------------

/**
 * A list of short strings, added one at a time. Two uses, which is why it takes `suggestions`:
 * aliases (cap 3, no vocabulary) and tags (cap 20, offered from the brand's registry).
 *
 * Dedupe is case-insensitive because that is the writer's OWN identity rule for tags
 * (`_resolve_tag_ids` casefolds and trims), so accepting "Franchise" next to "franchise" here
 * would show two chips that the save then collapses into one — the form would appear to lose
 * an entry. Applying it to aliases too is harmless: two spellings of one alias fold to the same
 * normalized signal anyway.
 */
export function ChipListEditor({
  values, onChange, max, maxLength, placeholder, suggestions = [], disabled, hex,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  max: number;
  maxLength: number;
  placeholder: string;
  suggestions?: string[];
  disabled?: boolean;
  hex: string;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const atMax = values.length >= max;

  const commit = (raw: string) => {
    const v = raw.trim().slice(0, maxLength);
    if (!v || atMax) return;
    if (values.some((x) => x.trim().toLowerCase() === v.toLowerCase())) { setDraft(''); return; }
    onChange([...values, v]);
    setDraft('');
  };

  const unused = suggestions.filter(
    (s) => !values.some((v) => v.trim().toLowerCase() === s.trim().toLowerCase()),
  );

  // Typeahead over the brand's existing vocabulary (2026-09-23). Tags are FOLDED on save
  // (`_resolve_tag_ids` casefolds and trims), so "Bruxelles" typed a second time with
  // different capitalisation is not a new tag — it silently joins the existing one. Saying
  // so while the user types is the only place that reads as an answer rather than a
  // surprise, and it is also what keeps the registry from filling with near-duplicates
  // nobody can then filter on.
  const q = draft.trim().toLowerCase();
  const matches = q ? suggestions.filter((s) => s.toLowerCase().includes(q)).slice(0, 6) : [];
  const exact = suggestions.some((s) => s.trim().toLowerCase() === q);
  // Gated on there BEING a vocabulary: with no `suggestions` (the alias boxes) the dropdown
  // would degrade to a lone "Créer le tag" row on a field that has no tags.
  const showDropdown = !!q && suggestions.length > 0 && !atMax;

  return (
    <Box sx={{ position: 'relative' }}>
      {values.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
          {values.map((v) => (
            <Box
              key={v}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.25, height: 22,
                bgcolor: alpha(hex, 0.09), color: hex,
                fontSize: 12, fontWeight: 600, pl: 1, pr: 0.375, borderRadius: '6px',
                maxWidth: '100%',
              }}
            >
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</Box>
              <IconButton
                size="small"
                disabled={disabled}
                onClick={() => onChange(values.filter((x) => x !== v))}
                sx={{ p: 0.125, color: alpha(hex, 0.6), '&:hover': { color: hex, bgcolor: 'transparent' } }}
              >
                <CloseIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
      <TextField
        size="small"
        fullWidth
        value={draft}
        // A comma commits too: these are pasted out of spreadsheets as comma-separated lists,
        // and the generic multi-value columns elsewhere in this page already split on commas.
        onChange={(e) => {
          const v = e.target.value;
          if (v.includes(',')) { v.split(',').forEach(commit); return; }
          setDraft(v.slice(0, maxLength));
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(draft); }
          // Backspace on an empty box removes the last chip — the standard behaviour of every
          // tag input, and the only way to fix a typo without reaching for the mouse.
          if (e.key === 'Backspace' && !draft && values.length) onChange(values.slice(0, -1));
        }}
        onBlur={() => commit(draft)}
        placeholder={atMax ? t('cibles.chips.at_max', { max }) : placeholder}
        disabled={disabled || atMax}
        sx={inputSx}
      />
      {showDropdown && (
        // `onMouseDown` preventDefault, not `onClick` alone: a click steals focus first, and
        // the input's `onBlur` commits the draft — so the row would add BOTH the half-typed
        // string and the suggestion.
        <Box
          onMouseDown={(e) => e.preventDefault()}
          sx={{
            ...dropdownSx,
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 600, mt: 0.5,
          }}
        >
          {matches.map((s) => {
            const taken = values.some((v) => v.trim().toLowerCase() === s.trim().toLowerCase());
            return (
              <Box
                key={s}
                onClick={() => { if (!disabled && !taken) commit(s); }}
                sx={{
                  px: 1.5, py: 0.875, display: 'flex', alignItems: 'center', gap: 0.75,
                  fontSize: 12.5, cursor: taken ? 'default' : 'pointer',
                  color: taken ? K.ink3 : K.ink,
                  borderBottom: `1px solid ${K.line}`,
                  '&:hover': { bgcolor: taken ? 'transparent' : alpha(hex, 0.06) },
                }}
              >
                {taken && <CheckCircleOutlineIcon sx={{ fontSize: 13, color: hex, flexShrink: 0 }} />}
                <Box component="span" sx={{ fontWeight: 600, color: taken ? 'text.secondary' : hex }}>{s}</Box>
                {taken && <Box component="span">{t('cibles.chips.already')}</Box>}
              </Box>
            );
          })}
          {!exact && (
            <Box
              onClick={() => !disabled && commit(draft)}
              sx={{
                px: 1.5, py: 0.875, fontSize: 12.5, cursor: 'pointer', color: K.ink2,
                '&:hover': { bgcolor: alpha(hex, 0.06) },
              }}
            >
              {t('cibles.chips.create', { value: draft.trim() })}
            </Box>
          )}
        </Box>
      )}
      {unused.length > 0 && !atMax && !q && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.625, alignItems: 'center' }}>
          <Typography sx={{ fontSize: 11.5, color: K.ink3 }}>
            {t('cibles.chips.suggestions')}
          </Typography>
          {unused.map((s) => (
            <Box
              key={s}
              onClick={() => !disabled && commit(s)}
              sx={{
                cursor: 'pointer', fontSize: 11.5, fontWeight: 500, color: K.ink2, height: 20, lineHeight: '18px',
                border: '1px dashed rgba(0,0,0,.18)', px: '7px', borderRadius: '6px',
                '&:hover': { borderColor: hex, color: hex },
              }}
            >
              {s}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// TagChips — read-only tag chip list
// ---------------------------------------------------------------------------

/** Read-only in the list: a tag is edited in the fiche, where the whole vocabulary is offered. */
export function TagChips({ names }: { names: string[] }) {
  if (names.length === 0) return null;
  return (
    <>
      {names.map((n) => (
        <Pill key={n} tone="tag" maxWidth={140}>
          {n}
        </Pill>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// RelatedProductsPicker — produit/collection references of a contenu (2026-10-06)
// ---------------------------------------------------------------------------

/** One reference. `label` is the row's name, else its url; `unresolved` = the by-id lookup
 *  found no such row (deleted since, or a legacy shard) — the link is kept, never dropped. */
export type RelatedRef = {
  id: string;
  label?: string;
  unresolved?: boolean;
  /** By-id lookup in flight / failed — never shown as a bare uuid (2026-10-08). */
  status?: 'loading' | 'failed';
};

/**
 * Search-backed, not a dropdown: a brand can hold 500 000 produit rows, so a list would be
 * a silent truncation and the user would read « mon produit n'est pas dans Traaker » off a
 * page boundary. The search is EXPLICIT (Enter / the button) exactly like the Cibles page's
 * own box — each call is one invoke of the shared targets-search Lambda, charged to the
 * `targets_search` budget, so per-keystroke is not an option.
 *
 * One call for both categories: `/targets/search` is asked with `categories=produit,collection`,
 * which the search filters PER ROW before its 50-hit limit — a client-side filter after the
 * limit let article titles fill the slots and showed « aucun résultat » for a product that
 * exists.
 *
 * A reference loaded from an existing cible arrives as a bare id; the dialog resolves it
 * through `POST …/targets/by-id` when it opens (label, else url, else « Cible non résolue »).
 */
export function RelatedProductsPicker({
  brandId, value, onChange, disabled, hex,
}: {
  brandId: string;
  value: RelatedRef[];
  onChange: (next: RelatedRef[]) => void;
  disabled?: boolean;
  hex: string;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<TargetRow[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState(false);
  const atMax = value.length >= TARGET_LIMITS.maxRelatedTargets;

  const run = async () => {
    const term = q.trim();
    if (!term || searching) return;
    setSearching(true); setFailed(false);
    try {
      const data = await api.get(
        `/brands/${brandId}/targets/search?q=${encodeURIComponent(term)}&categories=produit,collection`,
      ) as { results?: TargetRow[] };
      setResults(data.results ?? []);
    } catch {
      // A throttled or failed search must NOT repaint an empty list — « aucun résultat »
      // reads as "this product does not exist", which is the opposite of what happened.
      setFailed(true);
    } finally {
      setSearching(false);
    }
  };

  return (
    <Box>
      {value.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
          {value.map((r) => (
            <Tooltip
              key={r.id}
              title={
                r.status === 'loading' ? ''
                  : r.status === 'failed' ? t('cibles.related.lookup_failed_hint')
                  : r.unresolved ? r.id : r.label ? '' : t('cibles.related.id_only')
              }
            >
              <Box
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.25, height: 22,
                  bgcolor: alpha(hex, 0.09), color: hex,
                  fontSize: 12, fontWeight: 600, pl: 1, pr: 0.375, borderRadius: '6px',
                  maxWidth: '100%',
                }}
              >
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: r.unresolved || r.status || !r.label ? 'italic' : undefined }}>
                  {r.status === 'loading' ? t('cibles.related.loading')
                    : r.status === 'failed' ? t('cibles.related.lookup_failed')
                    : r.unresolved ? t('cibles.related.unresolved')
                    : (r.label ?? t('cibles.related.unnamed'))}
                </Box>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => onChange(value.filter((x) => x.id !== r.id))}
                  sx={{ p: 0.125, color: 'inherit', opacity: 0.55, '&:hover': { opacity: 1 } }}
                >
                  <CloseIcon sx={{ fontSize: 11 }} />
                </IconButton>
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 0.75 }}>
        <TextField
          size="small"
          fullWidth
          value={q}
          disabled={disabled || atMax}
          onChange={(e) => setQ(e.target.value)}
          // Enter searches — it must NOT fall through to the dialog's save, which is what
          // every other text field in the dialog does.
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); run(); } }}
          placeholder={t('cibles.related.placeholder')}
          sx={inputSx}
        />
        <Button
          variant="outlined" size="small" onClick={run} disabled={disabled || atMax || !q.trim() || searching}
          sx={[secondaryBtnSx, { flexShrink: 0, minWidth: 86 }]}
        >
          {searching ? <CircularProgress size={13} color="inherit" /> : t('cibles.related.search')}
        </Button>
      </Box>
      {atMax && (
        <Typography sx={{ display: 'block', mt: 0.5, fontSize: 11.5, color: K.ink3 }}>
          {t('cibles.related.at_max', { max: TARGET_LIMITS.maxRelatedTargets })}
        </Typography>
      )}
      {failed && (
        <Typography sx={{ display: 'block', mt: 0.5, fontSize: 11.5, color: K.warningInk }}>
          {t('cibles.related.search_failed')}
        </Typography>
      )}
      {results !== null && !atMax && (
        <Box sx={{ mt: 0.75, maxHeight: 180, overflowY: 'auto', border: `1px solid ${K.line}`, borderRadius: '10px' }}>
          {results.length === 0 ? (
            <Typography sx={{ display: 'block', px: 1.5, py: 1, fontSize: 12.5, color: K.ink3 }}>
              {t('cibles.related.no_result')}
            </Typography>
          ) : results.map((r) => {
            const picked = value.some((x) => x.id === r.targetId);
            return (
              <Box
                key={r.targetId}
                onClick={() => { if (!picked) onChange([...value, { id: r.targetId, label: r.label }]); }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
                  cursor: picked ? 'default' : 'pointer', opacity: picked ? 0.45 : 1,
                  borderBottom: `1px solid ${K.line}`,
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { bgcolor: picked ? undefined : K.rowHover },
                }}
              >
                <Typography sx={{ flex: 1, fontSize: 13, color: K.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.label}
                </Typography>
                <Pill>{t(`cibles.tab.${r.category}`)}</Pill>
                {picked && <CheckCircleOutlineIcon sx={{ fontSize: 13, color: hex }} />}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ParentValue — the selected parent marque inside a dialog's <Select>
// ---------------------------------------------------------------------------

/** Shared by every dialog's « Marque » select. `emptyKey` = the placeholder for no parent. */
export function ParentValue({ marques, value, emptyKey = 'cibles.parent_none' }: { marques: TargetRow[]; value: string; emptyKey?: string }) {
  const { t } = useTranslation();
  const brand = marques.find((m) => m.targetId === value);
  if (!brand) return <Typography component="span" sx={{ color: K.ink3, fontSize: 13 }}>{t(emptyKey)}</Typography>;
  return <Pill sx={{ color: K.ink, fontSize: 12 }}>{brand.label}</Pill>;
}

// ---------------------------------------------------------------------------
// Content type constants for the contenu dialog
// ---------------------------------------------------------------------------

const CONTENU_FORMAT_TYPES = ['article', 'blog', 'newsletter', 'podcast', 'guide', 'tutorial', 'benchmark', 'presse', 'autre'] as const;

const CONTENT_TYPE_ICON_SX = { fontSize: 15, opacity: 0.7 };
function ContentTypeIcon({ type }: { type: string }) {
  switch (type) {
    case 'article':   return <ArticleOutlinedIcon sx={CONTENT_TYPE_ICON_SX} />;
    case 'blog':      return <RssFeedIcon sx={CONTENT_TYPE_ICON_SX} />;
    case 'newsletter': return <MailOutlineIcon sx={CONTENT_TYPE_ICON_SX} />;
    case 'podcast':   return <PodcastsIcon sx={CONTENT_TYPE_ICON_SX} />;
    case 'guide':     return <MenuBookOutlinedIcon sx={CONTENT_TYPE_ICON_SX} />;
    case 'tutorial':  return <SchoolOutlinedIcon sx={CONTENT_TYPE_ICON_SX} />;
    case 'benchmark': return <EqualizerIcon sx={CONTENT_TYPE_ICON_SX} />;
    case 'presse':    return <NewspaperIcon sx={CONTENT_TYPE_ICON_SX} />;
    case 'autre':     return <MoreHorizIcon sx={CONTENT_TYPE_ICON_SX} />;
    default:          return null;
  }
}

// ---------------------------------------------------------------------------
// GenericTargetDialog — add/edit dialog for all non-custom categories
// (marque, offre, produit, collection, contenu — NOT établissement or personne)
// ---------------------------------------------------------------------------

export function GenericTargetDialog({
  open, mode = 'add', cat, initialRow, marques, onSave, onClose, saving, tagRegistry, brandId,
}: {
  open: boolean;
  mode?: 'add' | 'edit';
  cat: CatConfig;
  initialRow?: TargetRow;
  marques: TargetRow[];
  onSave: (data: { label: string; parentTargetId?: string; fields: { name: string; value: any }[]; tags?: string[]; relatedTargetIds?: string[] }) => Promise<void>;
  onClose: () => void;
  saving: boolean;
  tagRegistry: TagRegistry;
  /** Needed only by the produit picker (contenu). Without it the picker is not rendered. */
  brandId?: string;
}) {
  const { t } = useTranslation();
  const isEdit = mode === 'edit';

  const initialVals = (): Record<string, string | string[]> => {
    const out: Record<string, string | string[]> = {};
    for (const col of cat.cols) {
      if (col.multi) {
        out[col.field] = initialRow
          ? initialRow.fields.filter((f) => f.name === col.field).map((f) => String(f.value)).filter(Boolean)
          : [];
      } else {
        out[col.field] = initialRow
          ? (initialRow.fields.find((f) => f.name === col.field)?.value as string ?? '')
          : '';
      }
    }
    return out;
  };

  // `loading` until the by-id lookup below answers: a chip never shows a bare uuid.
  const initialRelated = (): RelatedRef[] =>
    (initialRow?.relatedTargetIds ?? []).map((id) => ({ id, status: 'loading' as const }));

  const [vals, setVals] = useState<Record<string, string | string[]>>(initialVals);
  const [parentId, setParentId] = useState(initialRow?.parentTargetId ?? (marques.length === 1 ? marques[0].targetId : ''));
  const [tags, setTags] = useState<string[]>(initialRow ? tagNamesOf(initialRow, tagRegistry) : []);
  // Round-tripped even when the picker is not rendered: the save is an upsert that rewrites
  // the whole home row, so dropping the state would silently clear the links.
  const [related, setRelated] = useState<RelatedRef[]>(initialRelated);

  useEffect(() => {
    if (!open) return;
    setVals(initialVals());
    setParentId(initialRow?.parentTargetId ?? (marques.length === 1 ? marques[0].targetId : ''));
    setTags(initialRow ? tagNamesOf(initialRow, tagRegistry) : []);
    setRelated(initialRelated());
  }, [open]);

  // Links loaded from an existing contenu are bare ids: resolve them once per opening.
  // Merged by id into the CURRENT list, so a chip added or removed meanwhile survives.
  useEffect(() => {
    const ids = initialRow?.relatedTargetIds ?? [];
    if (!open || !brandId || ids.length === 0) return;
    let cancelled = false;
    targetsService
      .getByIds(brandId, ids.map((targetId) => ({ targetId })))
      .then((resp) => {
        if (cancelled) return;
        const found = new Map(resp.items.map((i) => [i.targetId, i]));
        const missing = new Set(resp.notFound);
        setRelated((prev) => prev.map((r) => {
          if (r.status !== 'loading') return r;
          const hit = found.get(r.id);
          if (hit) return { id: r.id, label: hit.label || hit.url || undefined };
          return missing.has(r.id) ? { id: r.id, unresolved: true } : { id: r.id, status: 'failed' };
        }));
      })
      .catch(() => {
        if (cancelled) return;
        setRelated((prev) => prev.map((r) => (r.status === 'loading' ? { id: r.id, status: 'failed' } : r)));
      });
    return () => { cancelled = true; };
  }, [open]);

  const handleSave = async () => {
    const fields: { name: string; value: any }[] = [];
    for (const col of cat.cols) {
      const v = vals[col.field];
      if (col.multi) {
        for (const s of v as string[]) {
          if (s.trim()) fields.push({ name: col.field, value: s.trim() });
        }
      } else if ((v as string).trim()) {
        fields.push({ name: col.field, value: (v as string).trim() });
      }
    }
    const nameVal = (vals['name'] as string ?? '').trim();
    const urlVal = (vals['url'] as string ?? '').trim();
    const label = nameVal || urlVal || t('cibles.unnamed');
    await onSave({
      label, parentTargetId: parentId || undefined, fields, tags,
      relatedTargetIds: related.map((r) => r.id),
    });
  };

  // First required text/url field for Enter-to-save and autoFocus
  const firstRequired = cat.cols.find((c: ColDef) => !c.optional && c.type !== 'zone');

  const renderColInput = (col: ColDef) => {
    if (col.multi) return (
      <ChipListEditor
        values={vals[col.field] as string[]}
        onChange={(next) => setVals((p) => ({ ...p, [col.field]: next }))}
        max={col.field === 'alias' ? TARGET_LIMITS.maxMarqueAliases : 10}
        maxLength={120}
        placeholder={t(col.placeholderKey)}
        disabled={saving}
        hex={cat.color}
      />
    );
    if (col.type === 'enum' && col.field === 'contentType') return (
      // Grouped icon Select: format types visible, platform types removed from UI.
      // Empty option = server-side derivation from url host (D42, never re-derived on browser).
      <Select
        size="small" fullWidth displayEmpty disabled={saving}
        value={vals[col.field] as string}
        onChange={(e) => setVals((p) => ({ ...p, [col.field]: e.target.value as string }))}
        renderValue={(v) => v ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ContentTypeIcon type={v as string} />
            {t(`cibles.contentType.${v}`)}
          </Box>
        ) : <Box sx={{ color: K.ink3 }}>{t(col.placeholderKey)}</Box>}
        sx={selectSx}
      >
        <MenuItem value="" sx={{ fontSize: 13, color: K.ink3 }}>
          {t(col.placeholderKey)}
        </MenuItem>
        {CONTENU_FORMAT_TYPES.map((v) => (
          <MenuItem key={v} value={v} sx={{ fontSize: 13 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ContentTypeIcon type={v} />
              {t(`cibles.contentType.${v}`)}
            </Box>
          </MenuItem>
        ))}
      </Select>
    );
    if (col.type === 'enum') return (
      <Select
        size="small" fullWidth displayEmpty disabled={saving}
        value={vals[col.field] as string}
        onChange={(e) => setVals((p) => ({ ...p, [col.field]: e.target.value as string }))}
        sx={selectSx}
      >
        <MenuItem value="" sx={{ fontSize: 13, color: K.ink3 }}>
          {t(col.placeholderKey)}
        </MenuItem>
        {(FIELD_REGISTRY[cat.id]?.[col.field]?.values ?? []).map((v) => (
          <MenuItem key={v} value={v} sx={{ fontSize: 13 }}>
            {t(`cibles.contentType.${v}`)}
          </MenuItem>
        ))}
      </Select>
    );
    if (col.type === 'date') return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small" fullWidth type="date" disabled={saving}
          value={vals[col.field] as string}
          onChange={(e) => setVals((p) => ({ ...p, [col.field]: e.target.value }))}
          inputProps={{ onClick: (e: React.MouseEvent<HTMLInputElement>) => (e.currentTarget as any).showPicker?.() }}
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          sx={inputSx}
        />
        <Button
          size="small" variant="outlined" disabled={saving}
          onClick={() => setVals((p) => ({ ...p, [col.field]: new Date().toISOString().split('T')[0] }))}
          sx={{ flexShrink: 0, height: 32, fontSize: 12, textTransform: 'none', whiteSpace: 'nowrap', borderColor: '#D1D5DB', color: K.ink2, px: 1.25, '&:hover': { borderColor: K.ink3, bgcolor: 'transparent' } }}
        >
          {t('cibles.today')}
        </Button>
      </Box>
    );
    return (
      <TextField
        size="small" fullWidth
        autoFocus={col.field === firstRequired?.field}
        value={vals[col.field] as string}
        onChange={(e) => setVals((p) => ({ ...p, [col.field]: e.target.value }))}
        onBlur={() => {
          if (col.type === 'url' || col.field === 'domain') {
            const s = vals[col.field] as string;
            if (s) {
              const { value } = normalizeDomainInput(s);
              if (value) setVals((p) => ({ ...p, [col.field]: value }));
            }
          }
        }}
        onKeyDown={(e) => e.key === 'Enter' && !col.multi && handleSave()}
        placeholder={t(col.placeholderKey)}
        type={col.type === 'email' ? 'email' : 'text'}
        error={(col.type === 'url' || col.field === 'domain') && !!(vals[col.field] as string) && !normalizeDomainInput(vals[col.field] as string).valid}
        helperText={(col.type === 'url' || col.field === 'domain') && !!(vals[col.field] as string) && !normalizeDomainInput(vals[col.field] as string).valid ? t('cibles.url_invalid') : undefined}
        sx={inputSx}
      />
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
      <DialogHead
        title={isEdit ? t('cibles.actions.edit') : t(cat.addLabelKey)}
        subtitle={isEdit ? initialRow?.label : undefined}
        onClose={onClose}
        disabled={saving}
      />

      <DialogContent sx={dialogContentSx}>

        {/* Parent brand */}
        {cat.hasParent && (
          <Box>
            <FieldLabel optional>{t('cibles.parent_column')}</FieldLabel>
            <Select size="small" value={parentId} onChange={(e) => setParentId(e.target.value as string)} displayEmpty fullWidth
              renderValue={(value) => <ParentValue marques={marques} value={value as string} />}
              sx={selectSx}>
              <MenuItem value="" sx={{ fontSize: 13, color: K.ink3 }}>{t('cibles.parent_none')}</MenuItem>
              {marques.map((m) => <MenuItem key={m.targetId} value={m.targetId} sx={{ fontSize: 13 }}>{m.label}</MenuItem>)}
            </Select>
          </Box>
        )}

        {/* Fields — contenu: URL full-width, then Type + Date side by side; others: generic stack */}
        {cat.id === 'contenu' ? (
          <>
            {cat.cols.filter((c) => c.field === 'url').map((col) => (
              <Box key={col.field}>
                <FieldLabel optional={col.optional}>{t(col.labelKey)}</FieldLabel>
                {renderColInput(col)}
              </Box>
            ))}
            <Box sx={{ display: 'flex', gap: 2 }}>
              {cat.cols.filter((c) => c.field === 'contentType' || c.field === 'publishedAt').map((col) => (
                <Box key={col.field} sx={{ flex: 1, minWidth: 0 }}>
                  <FieldLabel optional={col.optional}>{t(col.labelKey)}</FieldLabel>
                  {renderColInput(col)}
                </Box>
              ))}
            </Box>
          </>
        ) : (
          cat.cols.map((col: ColDef) => (
            <Box key={col.field}>
              <FieldLabel optional={col.optional}>{t(col.labelKey)}</FieldLabel>
              {renderColInput(col)}
            </Box>
          ))
        )}

        {/* Produits liés + Tags — side by side for contenu, full-width tags otherwise */}
        {cat.id === 'contenu' && brandId ? (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <FieldLabel optional>{t('cibles.related.label')}</FieldLabel>
              <RelatedProductsPicker
                brandId={brandId}
                value={related}
                onChange={setRelated}
                disabled={saving}
                hex={cat.color}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <FieldLabel optional>{t('cibles.tags.label')}</FieldLabel>
              <ChipListEditor
                values={tags}
                onChange={setTags}
                max={TARGET_LIMITS.maxTagsPerTarget}
                maxLength={TARGET_LIMITS.maxTagLength}
                placeholder={t('cibles.tags.placeholder')}
                suggestions={[...tagRegistry.values()]}
                disabled={saving}
                hex={cat.color}
              />
            </Box>
          </Box>
        ) : (
          <Box>
            <FieldLabel optional>{t('cibles.tags.label')}</FieldLabel>
            <ChipListEditor
              values={tags}
              onChange={setTags}
              max={TARGET_LIMITS.maxTagsPerTarget}
              maxLength={TARGET_LIMITS.maxTagLength}
              placeholder={t('cibles.tags.placeholder')}
              suggestions={[...tagRegistry.values()]}
              disabled={saving}
              hex={cat.color}
            />
          </Box>
        )}

      </DialogContent>

      <DialogFoot>
        <Button variant="outlined" size="small" onClick={onClose} disabled={saving} sx={secondaryBtnSx}>
          {t('cibles.actions.cancel')}
        </Button>
        <Button variant="contained" size="small" disableElevation onClick={handleSave} disabled={saving} sx={primaryBtnSx}>
          {saving ? <CircularProgress size={14} color="inherit" /> : (isEdit ? t('cibles.actions.save') : t('cibles.actions.add'))}
        </Button>
      </DialogFoot>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// URL helpers for product / contenu domain views
// ---------------------------------------------------------------------------

/** Extract unique domains from a product's URL fields, capped at 3 for display. */
export function productDomains(row: TargetRow): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const f of row.fields) {
    if (f.name !== 'url' || typeof f.value !== 'string') continue;
    const d = extractDomain(f.value as string);
    if (d && !seen.has(d)) { seen.add(d); out.push(d); }
    if (out.length >= 3) break;
  }
  return out;
}

export function extractDomain(url: string): string {
  try {
    const full = url.startsWith('http') ? url : `https://${url}`;
    return new URL(full).hostname.replace(/^www\./, '');
  } catch {
    return url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0] || url;
  }
}

/**
 * Strip subdomain prefix from a host, returning the registrable apex domain.
 * `blog.dessange.com` → `dessange.com`; `dessange.co.uk` → `dessange.co.uk`.
 * Uses `registrableLabel` so two-label TLDs (co.uk, com.au…) are handled correctly.
 */
export function apexDomain(host: string): string {
  const label = registrableLabel(host);
  if (!label) return host;
  // Use lastIndexOf so a label that also appears in a subdomain (rare) doesn't truncate early
  const idx = host.lastIndexOf(label);
  return idx >= 0 ? host.slice(idx) : host;
}

export function getUrlPath(url: string, domain: string): string {
  try {
    const full = url.startsWith('http') ? url : `https://${url}`;
    const u = new URL(full);
    const path = u.pathname + (u.search || '') + (u.hash || '');
    return path === '/' ? '' : path;
  } catch {
    const idx = url.indexOf(domain);
    return idx >= 0 ? url.slice(idx + domain.length) : '';
  }
}

/**
 * Path to show for a URL inside a contenu group, relative to the group's apex domain.
 * For same-apex URLs: `/path` (just the pathname).
 * For subdomain URLs: `subdomain/path` — the subdomain prefix makes the source
 * identifiable within the group without repeating the apex domain.
 */
function getUrlPathInGroup(url: string, apex: string): string {
  try {
    const full = url.startsWith('http') ? url : `https://${url}`;
    const u = new URL(full);
    const host = u.hostname.replace(/^www\./, '');
    const path = u.pathname + (u.search || '') + (u.hash || '');
    const pathPart = path === '/' ? '' : path;
    if (host !== apex && host.endsWith('.' + apex)) {
      const sub = host.slice(0, -(apex.length + 1));
      return `${sub}${pathPart || '/'}`;
    }
    return pathPart || '/';
  } catch {
    return url;
  }
}

// ---------------------------------------------------------------------------
// ProductListView — one row per product with domain favicon + expandable URLs
// ---------------------------------------------------------------------------

export function ProductListView({
  rows, locked, onDelete, onEdit, marques,
}: {
  rows: TargetRow[];
  locked: boolean;
  onDelete: (targetId: string) => void;
  onEdit: (row: TargetRow) => void;
  marques: TargetRow[];
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setExpanded((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  if (!rows.length) return null;

  return (
    <Box>
      {rows.map((row) => {
        const isOpen = expanded.has(row.targetId);
        const urlFields = row.fields.filter((f) => f.name === 'url' && typeof f.value === 'string');
        const idVal = row.fields.find((f) => f.name === 'id')?.value as string | undefined;
        const domains = productDomains(row);

        // Brand color — for a very subtle bg tint and action hover
        const parentMarque = marques.find((m) => m.targetId === row.parentTargetId);
        // Use explicit colour if set, else derive a deterministic per-brand colour from the
        // marque's targetId. This ensures two brands always look distinct even without a swatch.
        const marqueColorToken = parentMarque?.color ?? null;
        const marqueHex = colorHex(marqueColorToken ?? undefined);

        return (
          <Box key={row.targetId} sx={{ borderBottom: `1px solid ${K.line}`, '&:last-of-type': { borderBottom: 'none' },
            bgcolor: parentMarque ? alpha(marqueHex, 0.03) : 'transparent' }}>
            {/* Product row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, minHeight: 42, '&:hover': { bgcolor: K.rowHover } }}>

              {/* Name + ID — flex:1, truncated */}
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: K.ink, letterSpacing: '-0.006em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.label}
                </Typography>
                {idVal && idVal !== row.label && (
                  <Pill sx={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontWeight: 500, fontSize: 11 }}>
                    {idVal}
                  </Pill>
                )}
              </Box>

              {/* Domain favicons — horizontal, centred between name and URL count */}
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.375, alignItems: 'center', flexShrink: 0 }}>
                {domains.length > 0
                  ? domains.map((d) => <DomainFavicon key={d} domain={d} name={d} size={18} />)
                  : <Box sx={{ width: 18, height: 18, borderRadius: '5px', bgcolor: K.chipBg }} />}
              </Box>

              {/* URL count + warnings */}
              <WarnPill warnings={row.warnings} />
              <Box sx={{ fontSize: 12, color: K.ink2, fontWeight: 600, flexShrink: 0, minWidth: 44, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {urlFields.length} URL{urlFields.length !== 1 ? 's' : ''}
              </Box>

              {/* Actions */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                <IconButton size="small" onClick={() => toggle(row.targetId)} aria-expanded={isOpen} sx={rowActionSx()}>
                  <ChevronRightIcon sx={{ fontSize: 17, transition: 'transform 0.15s', transform: isOpen ? 'rotate(90deg)' : 'none' }} />
                </IconButton>
                <IconButton size="small" disabled={locked} onClick={() => onEdit(row)} aria-label={t('cibles.actions.edit')} sx={rowActionSx()}>
                  <EditOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
                <IconButton size="small" disabled={locked} onClick={() => onDelete(row.targetId)} aria-label={t('cibles.actions.delete')} sx={rowActionSx(true)}>
                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Box>
            </Box>

            {/* Expanded URL list */}
            {isOpen && urlFields.length > 0 && (
              <Box sx={{ bgcolor: K.headBg, borderTop: `1px solid ${K.line}` }}>
                {urlFields.map((f, idx) => {
                  const url = f.value as string;
                  const full = url.startsWith('http') ? url : `https://${url}`;
                  const domain = extractDomain(url);
                  const isBuying = !!(f as any).buyingPage;
                  return (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 5, pr: 2, py: 0.75,
                      borderBottom: idx < urlFields.length - 1 ? `1px solid ${K.line}` : 'none' }}>
                      <DomainFavicon domain={domain} name={domain} size={16} />
                      <Typography
                        component="a" href={full} target="_blank" rel="noopener noreferrer"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        sx={{ flex: 1, fontSize: 12, fontFamily: MONO, color: K.ink2,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none',
                          '&:hover': { color: K.primaryDark, textDecoration: 'underline' } }}
                      >
                        {url}
                      </Typography>
                      {isBuying && (
                        <Pill tone="ok">
                          <ShoppingCartOutlinedIcon sx={{ fontSize: 12 }} />
                          {t('cibles.produit.buying_page')}
                        </Pill>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ProductAddForm — add/edit dialog for a single product (2026-09-23)
// ---------------------------------------------------------------------------

export function ProductAddForm({
  open, mode = 'add', initialRow, marques, onSave, onClose, saving, tagRegistry,
}: {
  open: boolean;
  mode?: 'add' | 'edit';
  initialRow?: TargetRow;
  marques: TargetRow[];
  onSave: (data: { label: string; parentTargetId?: string; fields: { name: string; value: any; buyingPage?: boolean }[]; tags?: string[] }) => Promise<void>;
  onClose: () => void;
  saving: boolean;
  tagRegistry: TagRegistry;
}) {
  const { t } = useTranslation();
  const isEdit = mode === 'edit';
  const cat = CAT_BY_ID['produit'];

  const [parentId, setParentId] = useState('');
  const [productId, setProductId] = useState('');
  const [name, setName] = useState('');
  const [urls, setUrls] = useState<{ value: string; buyingPage: boolean }[]>([{ value: '', buyingPage: false }]);
  const [tags, setTags] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    if (isEdit && initialRow) {
      setParentId(initialRow.parentTargetId ?? '');
      setProductId((initialRow.fields.find((f) => f.name === 'id')?.value as string) ?? '');
      setName((initialRow.fields.find((f) => f.name === 'name')?.value as string) ?? '');
      setUrls(
        initialRow.fields
          .filter((f) => f.name === 'url' && typeof f.value === 'string')
          .map((f) => ({ value: f.value as string, buyingPage: !!(f as any).buyingPage }))
          .concat([]) // avoid empty list
          .filter((u) => u.value),
      );
      if (!initialRow.fields.some((f) => f.name === 'url')) setUrls([{ value: '', buyingPage: false }]);
      setTags(tagNamesOf(initialRow, tagRegistry));
    } else {
      setParentId(marques.length === 1 ? marques[0].targetId : '');
      setProductId('');
      setName('');
      setUrls([{ value: '', buyingPage: false }]);
      setTags([]);
    }
  }, [open]);

  const addUrl = () => setUrls((p) => [...p, { value: '', buyingPage: false }]);
  const removeUrl = (idx: number) => setUrls((p) => p.filter((_, i) => i !== idx));
  const setUrl = (idx: number, value: string) => setUrls((p) => p.map((u, i) => i === idx ? { ...u, value } : u));
  const setBuying = (idx: number, buyingPage: boolean) => setUrls((p) => p.map((u, i) => i === idx ? { ...u, buyingPage } : u));

  const handleSave = async () => {
    if (!productId.trim()) return;
    const validUrls = urls.filter((u) => u.value.trim());
    if (!validUrls.length) return;
    const fields: { name: string; value: any; buyingPage?: boolean }[] = [];
    fields.push({ name: 'id', value: productId.trim() });
    if (name.trim()) fields.push({ name: 'name', value: name.trim() });
    for (const u of validUrls) {
      const { value: norm } = normalizeProductUrl(u.value);
      fields.push({ name: 'url', value: norm || u.value.trim(), ...(u.buyingPage ? { buyingPage: true } : {}) });
    }
    const label = name.trim() || productId.trim();
    await onSave({ label, parentTargetId: parentId || undefined, fields, tags });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
      <DialogHead
        title={isEdit ? t('cibles.produit.edit_title') : t('cibles.add.produit')}
        subtitle={t('cibles.produit.subtitle')}
        onClose={onClose}
        disabled={saving}
      />

      <DialogContent sx={dialogContentSx}>

        {/* Brand */}
        {marques.length > 0 && (
          <Box>
            <FieldLabel optional>{t('cibles.parent_column')}</FieldLabel>
            <Select size="small" value={parentId} onChange={(e) => setParentId(e.target.value as string)} displayEmpty fullWidth
              renderValue={(v) => <ParentValue marques={marques} value={v as string} />}
              sx={selectSx}>
              <MenuItem value="" sx={{ fontSize: 13, color: K.ink3 }}>{t('cibles.parent_none')}</MenuItem>
              {marques.map((m) => <MenuItem key={m.targetId} value={m.targetId} sx={{ fontSize: 13 }}>{m.label}</MenuItem>)}
            </Select>
          </Box>
        )}

        {/* ID + Name */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <Box>
            <FieldLabel>{t('cibles.produit.id_label')}</FieldLabel>
            <TextField size="small" fullWidth autoFocus value={productId} onChange={(e) => setProductId(e.target.value)}
              placeholder={t('cibles.produit.id_ph')}
              sx={[inputSx, { '& .MuiInputBase-input': { fontFamily: MONO } }]} />
          </Box>
          <Box>
            <FieldLabel optional>{t('cibles.produit.name_label')}</FieldLabel>
            <TextField size="small" fullWidth value={name} onChange={(e) => setName(e.target.value)}
              placeholder={t('cibles.produit.name_ph')}
              sx={inputSx} />
          </Box>
        </Box>

        {/* URLs */}
        <Box>
          <FieldLabel>{t('cibles.produit.urls_label')}</FieldLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {urls.map((u, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <TextField size="small" sx={[inputSx, { flex: 1, '& .MuiInputBase-input': { fontFamily: MONO, fontSize: 12.5 } }]}
                  value={u.value}
                  onChange={(e) => setUrl(idx, e.target.value)}
                  onBlur={() => { const { value: v } = normalizeProductUrl(u.value); if (v) setUrl(idx, v); }}
                  onKeyDown={(e) => e.key === 'Enter' && addUrl()}
                  placeholder={t('cibles.produit.url_ph')}
                  error={!!u.value && !normalizeProductUrl(u.value).valid}
                />
                <Tooltip title={t('cibles.produit.buying_page_tooltip')}>
                  <Box
                    onClick={() => setBuying(idx, !u.buyingPage)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5, px: 1, height: 40, borderRadius: '10px',
                      border: '1px solid', borderColor: u.buyingPage ? 'rgba(16,185,129,.4)' : K.line,
                      bgcolor: u.buyingPage ? 'rgba(16,185,129,.08)' : K.paper,
                      color: u.buyingPage ? '#047857' : K.ink3,
                      cursor: 'pointer', userSelect: 'none', flexShrink: 0,
                      '&:hover': { borderColor: u.buyingPage ? 'rgba(16,185,129,.55)' : 'rgba(0,0,0,.18)' },
                    }}
                  >
                    <ShoppingCartOutlinedIcon sx={{ fontSize: 15 }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'inherit', whiteSpace: 'nowrap' }}>
                      {t('cibles.produit.buying_page')}
                    </Typography>
                  </Box>
                </Tooltip>
                {urls.length > 1 && (
                  <IconButton size="small" onClick={() => removeUrl(idx)} sx={rowActionSx(true)}>
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
          {urls.length < TARGET_LIMITS.maxProduitUrls && (
            <Button size="small" variant="text" startIcon={<AddIcon sx={{ fontSize: '15px !important' }} />} onClick={addUrl}
              sx={{ mt: 0.75, fontSize: 12.5, fontWeight: 600, borderRadius: '8px', color: K.primaryDark, px: 1, '&:hover': { bgcolor: K.primarySoft } }}>
              {t('cibles.produit.add_url')}
            </Button>
          )}
        </Box>

        {/* Tags */}
        <Box>
          <FieldLabel optional>{t('cibles.tags.label')}</FieldLabel>
          <ChipListEditor values={tags} onChange={setTags} max={TARGET_LIMITS.maxTagsPerTarget} maxLength={TARGET_LIMITS.maxTagLength}
            placeholder={t('cibles.tags.placeholder')} suggestions={[...tagRegistry.values()]} disabled={saving} hex={cat.color} />
        </Box>

      </DialogContent>

      <DialogFoot>
        <Button variant="outlined" size="small" onClick={onClose} disabled={saving} sx={secondaryBtnSx}>
          {t('cibles.actions.cancel')}
        </Button>
        <Button variant="contained" size="small" disableElevation onClick={handleSave}
          disabled={saving || !productId.trim() || !urls.some((u) => u.value.trim())}
          sx={primaryBtnSx}>
          {saving ? <CircularProgress size={14} color="inherit" /> : (isEdit ? t('cibles.actions.save') : t('cibles.actions.add'))}
        </Button>
      </DialogFoot>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// CollectionListView — compact list for catalogue category pages (2026-09-23)
// ---------------------------------------------------------------------------

export function CollectionListView({
  rows, locked, onDelete, onEdit, marques, tagRegistry,
}: {
  rows: TargetRow[];
  locked: boolean;
  onDelete: (targetId: string) => void;
  onEdit: (row: TargetRow) => void;
  marques: TargetRow[];
  tagRegistry: TagRegistry;
}) {
  const { t } = useTranslation();
  if (!rows.length) return null;
  const cat = CAT_BY_ID['collection'];

  return (
    <Box>
      {rows.map((row) => {
        const url = (row.fields.find((f) => f.name === 'url')?.value as string) ?? row.label ?? '';
        const name = (row.fields.find((f) => f.name === 'name')?.value as string) ?? '';
        const domain = extractDomain(url);
        const tagNames = tagNamesOf(row, tagRegistry);
        const parentMarque = marques.find((m) => m.targetId === row.parentTargetId);
        const marqueColorToken = parentMarque?.color ?? null;
        const marqueHex = colorHex(marqueColorToken ?? undefined);
        const hasWarn = (row.warnings?.length ?? 0) > 0;

        return (
          <Box
            key={row.targetId}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.25,
              px: 2, minHeight: 42,
              borderBottom: `1px solid ${K.line}`, '&:last-of-type': { borderBottom: 'none' },
              bgcolor: parentMarque ? alpha(marqueHex, 0.03) : 'transparent',
              '&:hover': { bgcolor: K.rowHover },
            }}
          >
            {/* Domain favicon */}
            <Box sx={{ flexShrink: 0 }}>
              <DomainFavicon domain={domain} name={domain} size={20} />
            </Box>

            {/* URL — truncated, never wraps */}
            <Typography
              component="a"
              href={url.startsWith('http') ? url : `https://${url}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{
                flex: 1, minWidth: 0,
                fontSize: 12.5, fontFamily: MONO, color: K.ink2,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                textDecoration: 'none', display: 'block',
                '&:hover': { color: K.primaryDark, textDecoration: 'underline' },
              }}
            >
              {url}
            </Typography>

            {/* Name badge */}
            {name && (
              <Pill tone="accent" hex={cat.color} maxWidth={220}>
                {name}
              </Pill>
            )}

            {/* Tags */}
            {tagNames.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.375, flexShrink: 0, flexWrap: 'nowrap', overflow: 'hidden' }}>
                <TagChips names={tagNames.slice(0, 3)} />
                {tagNames.length > 3 && (
                  <Typography sx={{ fontSize: 11.5, color: K.ink3, alignSelf: 'center' }}>+{tagNames.length - 3}</Typography>
                )}
              </Box>
            )}

            {/* Warning */}
            {hasWarn && <WarnPill warnings={row.warnings} />}

            {/* Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
              <IconButton size="small" disabled={locked} onClick={() => onEdit(row)} aria-label={t('cibles.actions.edit')} sx={rowActionSx()}>
                <EditOutlinedIcon sx={{ fontSize: 15 }} />
              </IconButton>
              <IconButton size="small" disabled={locked} onClick={() => onDelete(row.targetId)} aria-label={t('cibles.actions.delete')} sx={rowActionSx(true)}>
                <DeleteOutlineIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ContenuDomainView — domain-grouped display for the contenu tab (2026-09-23)
//
// Groups URL targets by registrable domain, shows each domain with its favicon
// and a collapsible list of paths.
// ---------------------------------------------------------------------------

export function ContenuDomainView({
  rows, locked, onDelete, onEdit, marques,
}: {
  rows: TargetRow[];
  locked: boolean;
  onDelete: (targetId: string) => void;
  onEdit: (row: TargetRow) => void;
  marques: TargetRow[];
}) {
  const { t } = useTranslation();
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());

  // Group by brand-then-APEX-domain: subdomains collapse into the root domain.
  // `blog.dessange.com` and `dessange.com` land in the same group keyed by
  // `dessange.com`.  Key format: `<parentTargetId|"none">::<apex>` — stable, unique.
  const grouped = useMemo(() => {
    const map = new Map<string, { domain: string; parentTargetId?: string; rows: TargetRow[] }>();
    for (const row of rows) {
      const url = (row.fields.find((f) => f.name === 'url')?.value as string) ?? row.label ?? '';
      const domain = apexDomain(extractDomain(url));
      const pid = row.parentTargetId ?? 'none';
      const key = `${pid}::${domain}`;
      if (!map.has(key)) map.set(key, { domain, parentTargetId: row.parentTargetId, rows: [] });
      map.get(key)!.rows.push(row);
    }
    // Sort by brand label (then domain) so the same brand stays together
    return [...map.values()].sort((a, b) => {
      const la = (marques.find((m) => m.targetId === a.parentTargetId)?.label ?? '') + a.domain;
      const lb = (marques.find((m) => m.targetId === b.parentTargetId)?.label ?? '') + b.domain;
      return la.localeCompare(lb);
    });
  }, [rows, marques]);

  const toggle = (domain: string) => {
    setExpandedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain); else next.add(domain);
      return next;
    });
  };

  if (rows.length === 0) return null;

  return (
    <Box>
      {grouped.map(({ domain, parentTargetId, rows: domainRows }) => {
        const key = `${parentTargetId ?? 'none'}::${domain}`;
        const expanded = expandedDomains.has(key);
        const parentMarque = parentTargetId ? marques.find((m) => m.targetId === parentTargetId) : undefined;

        return (
          <Box key={key} sx={{ borderBottom: `1px solid ${K.line}`, '&:last-of-type': { borderBottom: 'none' } }}>
            {/* Domain header row */}
            <Box
              onClick={() => toggle(key)}
              role="button"
              aria-expanded={expanded}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.25,
                px: 2, minHeight: 44, cursor: 'pointer', userSelect: 'none',
                '&:hover': { bgcolor: K.rowHover },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 17, color: K.ink3, flexShrink: 0, ml: -0.5, transition: 'transform 0.15s', transform: expanded ? 'rotate(90deg)' : 'none' }} />
              <DomainFavicon domain={domain} name={domain} size={18} />
              <Typography sx={{ fontFamily: K.tight, fontSize: 13.5, fontWeight: 700, color: K.ink, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {domain}
              </Typography>
              {/* Brand badge — shown when URLs are associated with a marque */}
              {parentMarque ? (
                <Pill sx={{ color: K.ink }} maxWidth={200}>
                  {parentMarque.label}
                </Pill>
              ) : (
                <Tooltip title={t('cibles.contenu.no_brand_hint')}>
                  <Box component="span" sx={{ fontSize: 11.5, color: K.ink3, flexShrink: 0, cursor: 'help' }}>
                    {t('cibles.contenu.no_brand')}
                  </Box>
                </Tooltip>
              )}
              <Box sx={{ minWidth: 28, textAlign: 'right', fontSize: 12, fontWeight: 600, color: K.ink2, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                {domainRows.length}
              </Box>
            </Box>

            {/* URL list */}
            {expanded && (
              <Box sx={{ bgcolor: K.paper, borderTop: `1px solid ${K.line}` }}>
                {/* Header — the three right-hand columns are unlabelled otherwise, and a
                    bare date next to a bare count is unreadable. Widths mirror the rows. */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 6.5, pr: 2, minHeight: 30, bgcolor: K.headBg, borderBottom: `1px solid ${K.line}` }}>
                  <Box sx={{ flex: 1 }} />
                  {[
                    { w: 96, k: 'cibles.col.contenu.contentType', align: 'left' as const },
                    { w: 84, k: 'cibles.col.contenu.publishedAt', align: 'left' as const },
                    { w: 48, k: 'cibles.col.contenu.citations', align: 'right' as const },
                  ].map((h) => (
                    <Box key={h.k} sx={{ ...microLabel, width: h.w, flexShrink: 0, textAlign: h.align }}>
                      {t(h.k)}
                    </Box>
                  ))}
                  {/* Spacer for the warning + edit + delete buttons */}
                  <Box sx={{ width: 60, flexShrink: 0 }} />
                </Box>
                {domainRows.map((row) => {
                  const url = (row.fields.find((f) => f.name === 'url')?.value as string) ?? row.label ?? '';
                  const path = getUrlPathInGroup(url, domain);
                  const hasWarn = (row.warnings?.length ?? 0) > 0;
                  const contentType = row.fields.find((f) => f.name === 'contentType')?.value as string | undefined;
                  const publishedAt = row.fields.find((f) => f.name === 'publishedAt')?.value as string | undefined;
                  return (
                    <Box
                      key={row.targetId}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        pl: 6.5, pr: 2, minHeight: 38,
                        borderBottom: `1px solid ${K.line}`,
                        '&:last-child': { borderBottom: 'none' },
                        '&:hover': { bgcolor: K.rowHover },
                      }}
                    >
                      <Typography
                        component="a"
                        href={url.startsWith('http') ? url : `https://${url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        sx={{
                          flex: 1, fontSize: 12.5, fontFamily: MONO,
                          color: K.ink2, overflow: 'hidden', textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap', textDecoration: 'none',
                          '&:hover': { color: K.primaryDark, textDecoration: 'underline' },
                        }}
                      >
                        {path || '/'}
                      </Typography>
                      {/* Type — « — » when absent, which only happens on a row written before
                          the field existed: a new one is derived from the host server-side. */}
                      <Box sx={{ width: 96, flexShrink: 0, minWidth: 0, display: 'flex' }}>
                        {contentType
                          ? <Pill maxWidth="100%" sx={{ fontWeight: 500 }}>{t(`cibles.contentType.${contentType}`)}</Pill>
                          : <Typography sx={{ fontSize: 12, color: K.ink3 }}>—</Typography>}
                      </Box>
                      <Typography sx={{ width: 84, flexShrink: 0, fontSize: 12, color: publishedAt ? K.ink2 : K.ink3, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        {publishedAt ?? '—'}
                      </Typography>
                      {/* Citations — no data source until Lot 5 writes the per-URL citation
                          counts. Rendered « — » with the reason, NEVER 0: a zero reads as
                          "this page is never cited", which is a finding we have not measured. */}
                      <Tooltip title={t('cibles.contenu.citations_pending')}>
                        <Typography sx={{ width: 48, flexShrink: 0, textAlign: 'right', fontSize: 12, color: K.ink3, cursor: 'help' }}>
                          —
                        </Typography>
                      </Tooltip>
                      {hasWarn && <WarnPill warnings={row.warnings} />}
                      <Tooltip title={t('cibles.actions.edit')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={locked}
                            onClick={(e) => { e.stopPropagation(); onEdit(row); }}
                            sx={rowActionSx()}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('cibles.actions.delete')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={locked}
                            onClick={(e) => { e.stopPropagation(); onDelete(row.targetId); }}
                            sx={rowActionSx(true)}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
